import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('qfl_token'));
  const [loading, setLoading] = useState(true);

  // Récupère le profil au démarrage si token présent
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('qfl_token');
      if (!storedToken) { setLoading(false); return; }

      try {
        const { data } = await api.get('/auth/me');
        setUser(data.user);
        setToken(storedToken);
      } catch {
        localStorage.removeItem('qfl_token');
        localStorage.removeItem('qfl_user');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);


  // Connexion
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('qfl_token', data.token);
    localStorage.setItem('qfl_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // Inscription
  const register = useCallback(async (pseudo, email, password, referralCode) => {
    const payload = { pseudo, email, password };
    if (referralCode) payload.referralCode = referralCode;
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('qfl_token', data.token);
    localStorage.setItem('qfl_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // Connexion avec un jeton déjà obtenu (connexion biométrique) : le profil
  // complet est ensuite rechargé par /auth/me au prochain rendu.
  const loginWithToken = useCallback((newToken, newUser) => {
    localStorage.setItem('qfl_token', newToken);
    localStorage.setItem('qfl_user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
    return newUser;
  }, []);

  // Déconnexion
  const logout = useCallback(() => {
    localStorage.removeItem('qfl_token');
    localStorage.removeItem('qfl_user');
    setToken(null);
    setUser(null);
  }, []);

  // Mise à jour du profil
  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('qfl_user', JSON.stringify(updatedUser));
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token,
    login,
    loginWithToken,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook d'accès au contexte
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
}

export default AuthContext;
